import styled from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';

// Positioning follows the disabled `Chat` widget (`components/Chat/Chat.styles.ts`)
// — same bottom-right fixed corner, same z-index (so the two would never overlap
// if both were ever enabled at once) — except `bottom` is 2.4rem instead of Chat's
// 0.8rem: this button is round with visible whitespace below it, and 0.8rem read
// as flush against the viewport edge (user feedback, 2026-09-23).
// Also shares that corner with the page's own scroll-to-top button
// (layouts/DefaultLayout/components/ScrollButton, "bottom: 3rem; right: 3rem;
// width/height: 4rem" — occupies roughly 3rem to 7rem off the bottom edge). At
// 2.4rem this button's own 5.6rem height (2.4rem to 8rem) overlapped it almost
// entirely once both were visible at once (reported: "жук закриває стрілочку
// догори"). 8rem clears ScrollButton's own top edge (7rem) with a 1rem gap,
// stacking this button above it instead of on top of it.
const FloatingButton = styled(Button)`
  position: fixed;
  bottom: 8rem;
  /* --support-ticket-scroll-lock-offset (SupportTicketModal.tsx) keeps this
     in place while the modal is open — without it, the button visually
     drifts right when the modal's open/close toggles the real browser
     scrollbar. Deliberately a feature-local CSS var, not the shared Modal's
     — this widget's modal is isolated from every other modal in the app. */
  right: calc(3rem + var(--support-ticket-scroll-lock-offset, 0px));
  z-index: 99999;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 5.6rem;
  height: 5.6rem;

  background-color: ${pickColor('colmarRed')};
  color: ${pickColor('white')};
  border-radius: 50%;
  box-shadow: 0 0.3rem 2.4rem -1rem ${pickColor('shadow')};

  transition: background-color ${({ theme }) => theme.transitionSpeed} ease-in;

  /* motive="m0" (no built-in motive) is used on this button specifically to
     skip Button's own "default" motive, which turns hover text/icon color
     bright red (${pickColor('red')}) — unreadable against this already-red
     background. Darkening the background on hover instead keeps the icon a
     stable white. */
  &:hover {
    background-color: ${pickColor('darkRed')};
  }

  span {
    font-size: 2.4rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  width: 44rem;
  padding: 2.4rem 2rem;

  /* Input's default motive ("m1"), TextArea's plain shared Label, and
     Select's own local Label component each style their <label> slightly
     differently (uppercase+1.2rem+gray vs capitalized+1.2rem+black vs
     uppercase+0.9rem+gray) — a pre-existing inconsistency across the design
     system that's only visible here because all three sit side by side in
     one small form. "&&" doubles this selector's specificity so it reliably
     wins over each of those, regardless of styled-components' stylesheet
     insertion order, without resorting to !important. */
  && label {
    margin-right: 0.4rem;

    font-size: 1.2rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1rem;

    color: ${pickColor('grayLabel')};
  }

  /* Subject/Description/Screenshot's labels are each wrapped in
     components/Label's own "Header" div — a "display: flex;
     align-items: center" row with its OWN fixed "height: 1.9rem"
     (Label.styles.ts), sized to fit the label plus an optional required-
     asterisk/error Headline next to it. Priority doesn't go through this
     wrapper at all (Select has its own plain, unwrapped label), which is
     why only Priority ever had a working label-to-field gap.
     Margin-bottom on the label ITSELF doesn't work here: a flex item's
     margin doesn't grow a FIXED-height flex container to fit it, so it
     just gets clipped at Header's edge instead of pushing the field below
     it down (confirmed in the browser: Subject's gap measured 0px this
     way). Margin-bottom directly on Header instead reliably pushes the
     next field down, since Header IS the actual flex item of Field/
     InputField — and leaving Header's own height/alignment untouched this
     way keeps the required-asterisk exactly where it was (an earlier
     attempt that changed Header's height to "auto" instead made the
     asterisk sink below the label — "зірочка з'їхала до низу").
     ":has(> label)" alone would also match Select's own Container (its
     Label is also a direct child) and wrongly add extra space AFTER the
     whole Priority field instead of doing nothing there — excluded via
     ":not(:has(> div))", since Select's Container also directly holds the
     dropdown's own div (Header's only other possible child is a plain
     <p>, from the optional asterisk/error Headline, never a div). */
  && div:has(> label):not(:has(> div)) {
    margin-bottom: 1rem;
  }
`;

// Headline defaults to `white-space: nowrap` (no `isWrapped`) — fine for the
// short English title, but Italian's translation ("Segnala un problema o un
// suggerimento") is long enough to run under Modal's absolutely-positioned
// close button instead of wrapping. `isWrapped` (set where this is used) lets
// it wrap to a second line; padding-right here keeps every line clear of the
// button regardless of language.
const Title = styled(Headline)`
  padding-right: 3.6rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

// A separate wrapper from the plain `Field` above, used only around Input
// and TextArea (never Select/Priority): react-select renders its own hidden
// <input> internally (for keyboard search-filtering) that stays invisible by
// default — scoping these `input`/`textarea` overrides to `Form` itself
// caught that hidden input too, un-hiding it as a small bordered box with a
// visible text caret sitting on top of the selected option's label (reported
// as a stray icon-like artifact next to "NORMAL").
const InputField = styled(Field)`
  /* Anchors CharacterCount's own "position: absolute" below — only actually
     used for the Description field (Subject has no counter), harmless here
     otherwise. */
  position: relative;

  /* Input's "m1" motive sets its input to font-weight: lighter plus color:
     black, while TextArea's textarea only sets font-weight: lighter itself
     (no color at all). "lighter" is a RELATIVE keyword (computed off the
     inherited weight), so the two can resolve to visibly different actual
     weights even with the same keyword. Pinning every value explicitly
     (numeric weight, not a keyword) makes the two identical regardless of
     ambient inheritance. */
  && input,
  && textarea {
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.6;

    color: ${pickColor('black')};
  }

  /* Neither Input nor TextArea set their own focus style, so the browser's
     default focus ring shows through — blue in every mainstream browser.
     Swapped for a black ring here to match the Select's "supportTicket"
     motive (selectMotives.data.ts), so all three fields focus consistently. */
  && input:focus,
  && textarea:focus {
    outline: none;
    border-color: ${pickColor('black')};
    box-shadow: 0 0 0 0.1rem ${pickColor('black')};
  }

  /* Description grows with its content (SupportTicketWidget.hooks.tsx's
     autoGrowDescription sets an explicit inline height on each keystroke) —
     "overflow: hidden" here stops the native scrollbar flashing on the
     height="auto" measuring step, and max-height caps runaway growth so a
     very long report scrolls inside the field instead of pushing the modal
     itself past the viewport. Capped at 14rem (was 24rem) — at a full
     1000-character description plus 5 attached screenshots, the old cap
     alone pushed the modal's total height past the viewport, forcing the
     outer BoxInner scroll just to reach the Submit button (user feedback,
     2026-09-24: fit everything "без скролу вертикального до низу"). The
     field still scrolls its own overflow internally beyond this height. */
  && textarea {
    min-height: 12rem;
    max-height: 14rem;
    overflow-y: auto;
    overflow-x: hidden;

    /* TextArea's own default border (borderColorDarkSecondary, #5A5F64) is
       noticeably darker than Input's m1 motive (borderLight, #cccccc) —
       side by side in this form the mismatch reads as a bug (reported: "рамка
       інпуту description не того самого кольору що і всі інші"). Matches
       Input's resting-state border here; focus-state is already unified
       above. */
    border-color: ${pickColor('borderLight')};
  }
`;

// Overlaid on the textarea's own bottom-right corner (position: absolute,
// taken out of flow) rather than sitting as its own line below it — even
// with margin-top removed, that line's own height still made the
// Description field taller than every other field, so its gap to Priority
// stayed visibly bigger than e.g. Subject's gap to Description (user
// feedback, 2026-09-24: make every field-to-field gap the same). Out of
// flow, InputField's height is just Label + textarea, same shape as every
// other field, so Form's shared "gap" alone produces an equal gap
// everywhere. The background chip keeps the digits legible over typed text
// that scrolls underneath; pointer-events: none keeps it from blocking
// clicks/selection in the textarea beneath it.
const CharacterCount = styled(Headline)`
  position: absolute;
  right: 1.2rem;
  bottom: 0.6rem;

  padding: 0 0.2rem;

  background-color: rgba(255, 255, 255, 0.85);

  pointer-events: none;
`;

// Same reasoning as CharacterCount above — Form's "gap" already spaces this
// from the Screenshot field above it; this own margin-top was stacking an
// extra, inconsistent gap on top of that.
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 0.8rem;
`;

const SubmitButton = styled(Button)`
  min-width: 14rem;
  margin-bottom: 0.8rem;
`;

// Same "button with an invisible file input layered on top" technique
// already used elsewhere in this app (Shop Images' Actions.styles.ts) — a
// relatively-positioned Button with an absolutely-positioned, opacity: 0
// <input type="file"> covering it, so the whole button area is clickable.
const ScreenshotButton = styled(Button)`
  position: relative;

  align-items: center;
  justify-content: center;

  width: fit-content;

  cursor: pointer;

  /* Button's own "icon-left ~ label" CSS only pads the LABEL's left side
     (0.3rem, Button.styles.ts) — nothing pushes the icon itself away from
     the text, so bumping icoarea up (SupportTicketWidget.tsx, to make the
     icon bigger) left them sitting edge to edge. Margin directly on the
     icon is the more reliable of the two ends to control, and doubles as
     the fix for their vertical alignment: IconLeft is a flex box with its
     own "height: 1em" (Button.styles.ts) sized off icoarea, which doesn't
     naturally line up with the label's own (taller) line-height — explicit
     self-alignment keeps the icon centered on the label's line regardless
     of that size difference. */
  .icon-left {
    align-self: center;
    margin-right: 0.6rem;
  }
`;

const ScreenshotFileInput = styled.input`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  width: 100%;
  height: 100%;

  opacity: 0;
  font-size: 0;

  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const ScreenshotHint = styled(Headline)`
  margin-top: 0.6rem;
`;

// Decision 16: up to 5 screenshots — a single horizontal row of
// ScreenshotPreview cards (user feedback, 2026-09-24: "давай всі фото
// горизонтально" — one row, not the earlier 3-then-2 grid). At the full 5
// cards (5 × 7rem thumbnail + 4 × 0.8rem gap = 38.2rem) this fits inside
// the form's own content area (44rem − 2 × 2rem padding = 40rem);
// "overflow-x: auto" is kept as a safety net in case that ever changes
// (a wider close-button badge, a form padding tweak) rather than
// something expected to trigger in normal use.
const ScreenshotList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 0.8rem;

  overflow-x: auto;
  padding-top: 0.6rem;

  // Now rendered below the "attach" button (user feedback, 2026-09-24) —
  // margin-top separates it from the button above; margin-bottom still
  // separates it from ScreenshotHint below.
  margin: 0.8rem 0;
`;

const ScreenshotPreview = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const ScreenshotThumbnail = styled.img`
  display: block;

  width: 7rem;
  height: 7rem;

  object-fit: cover;
  border: 0.1rem solid ${pickColor('borderColorDark')};
  border-radius: ${({ theme }) => theme.borderRadius.base};
`;

const ScreenshotRemoveButton = styled(Button)`
  position: absolute;
  top: -0.6rem;
  right: -0.6rem;

  align-items: center;
  justify-content: center;

  width: 2rem;
  height: 2rem;
  padding: 0;

  background-color: ${pickColor('white')};
  border: 0.1rem solid ${pickColor('borderColorDark')};
  border-radius: 50%;
  box-shadow: 0 0.1rem 0.4rem rgba(0, 0, 0, 0.15);

  color: ${pickColor('grayDark')};

  span {
    font-size: 1.2rem;
  }

  &:hover {
    color: ${pickColor('redSecondary')};
  }
`;

export {
  FloatingButton,
  Form,
  Title,
  Field,
  InputField,
  CharacterCount,
  Footer,
  SubmitButton,
  ScreenshotButton,
  ScreenshotFileInput,
  ScreenshotHint,
  ScreenshotList,
  ScreenshotPreview,
  ScreenshotThumbnail,
  ScreenshotRemoveButton,
};
